<template>
  <CommonModal ref="commonModal" compact @on-close="onModalClose">
    <div class="email-upsell-modal">
      <img :src="productBanner" class="email-upsell-modal__banner" alt="upsell-banner" />
      <div
        class="email-upsell-modal__content"
        :class="{ 'email-upsell-modal__content--stickers': !isTransfers }"
      >
        <UseSvg :icon="brandLogo" class="email-upsell-modal__brand-logo" />
        <template v-if="coupon">
          <div class="email-upsell-modal__coupon-container">
            <h2 class="email-upsell-modal__coupon-header">Your code is ready!</h2>
            <div class="email-upsell-modal__promo-code">
              {{ coupon }}
            </div>
            <p class="email-upsell-modal__coupon-sub-text">Use this code at checkout</p>
          </div>
          <button type="submit" class="email-upsell-modal__submit-btn" @click="closeModal">
            CONTINUE SHOPPING
            <UseSvg class="email-upsell-modal__btn-icon" icon="arrow-narrow-right-alt" />
          </button>
        </template>
        <template v-else>
          <h2 class="email-upsell-modal__offer-title">
            GET <span class="email-upsell-modal__offer-title--highlight">{{ percentOff }} OFF</span>
          </h2>
          <p class="email-upsell-modal__offer-subtitle">YOUR FIRST {{ upsellText }} ORDER!</p>
          <form class="email-upsell-modal__email-form" @submit.prevent="submitEmail">
            <input
              id="email"
              v-model="email"
              type="text"
              placeholder="Email"
              :class="{ 'email-upsell-modal__email-input--error': emailError }"
              class="email-upsell-modal__email-input"
              required
            />
            <span v-if="emailError" class="email-upsell-modal__error">
              Please enter a valid email address (xxx@yyy.zz)
            </span>
            <button type="submit" :disabled="disableSubmit" class="email-upsell-modal__submit-btn">
              <template v-if="isLoading">
                <Spinner theme="white" class="email-upsell-modal__spinner" /> LOADING...
              </template>

              <template v-else>
                GET {{ percentOff }} OFF
                <UseSvg
                  v-if="!disableSubmit"
                  class="email-upsell-modal__btn-icon"
                  icon="arrow-narrow-right-alt"
                />
              </template>
            </button>
          </form>
          <p class="email-upsell-modal__agreement">
            By entering your email, you agree to receive marketing emails and promotional
            communications from Jiffy. Please review our
            <a href="/privacypolicy" class="email-upsell-modal__agreement--highlight-link">
              Privacy Policy</a
            >
            and
            <a href="/termsofuse" class="email-upsell-modal__agreement--highlight-link">
              Terms of Use</a
            >
            for further information.
          </p>
          <div class="email-upsell-modal__footer">
            <div class="email-upsell-modal__delivery-info">
              <UseSvg icon="fast-delivery-special" class="email-upsell-modal__delivery-icon" />
              DELIVERED AS SOON AS TOMORROW
            </div>
            <span v-if="isTransfers" class="email-upsell-modal__learn-more">
              What is
              <a href="/jiffytransfers" class="email-upsell-modal__learn-more--highlight-link">
                Jiffy Transfers?</a
              >
            </span>
          </div>
        </template>
      </div>
    </div>
  </CommonModal>
</template>

<script>
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { api } from "jiffy-api";
import { writeCookie } from "common/utils/cookie";
import { track as rudderstackTrack } from "common/utils/rudderstack/tracker";
import Spinner from "components/jiffy/vue-cart/Spinner.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import CommonModal from "common/vue/components/CommonModal.vue";

export default {
  name: "EmailUpsellModal",
  components: { CommonModal, UseSvg, Spinner },
  props: {
    productType: {
      type: String,
      default: "stickers"
    }
  },
  data() {
    return {
      email: "",
      coupon: null,
      emailError: false,
      isLoading: false,
      emailPattern: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/,
      productImageUrls: {
        transfers: {
          icon: "jiffy-transfers-inline",
          banner:
            "https://jiffy.imgix.net/gen/cross-sell-stickers.jpg?auto=format&w=455&h=455&q=80&dpr=2"
        },
        stickers: {
          icon: "jiffy-stickers-inline",
          banner:
            "https://jiffy.imgix.net/gen/cross-sell-transfers.jpg?auto=format&w=455&h=455&q=80&dpr=2"
        }
      },
      promotionNames: {
        transfers: "Modal Email - Transfers 09-24",
        stickers: "Modal Email - Stickers 09-24"
      }
    };
  },
  computed: {
    brandLogo() {
      return this.productImageUrls[this.productType]?.icon;
    },
    productBanner() {
      return this.productImageUrls[this.productType]?.banner;
    },
    isTransfers() {
      return this.productType === "transfers";
    },
    promotionName() {
      return this.promotionNames[this.productType];
    },
    disableSubmit() {
      return this.isLoading || this.email.length < 3; // 3 is the smallest emailId possible
    },
    upsellText() {
      return this.isTransfers ? "JIFFY TRANSFERS" : "JIFFY STICKERS";
    },
    percentOff() {
      return this.isTransfers ? "20%" : "30%";
    }
  },
  mounted() {
    submitDtfEvent("guest_email_modal_shown", {
      page: "pdp",
      product: this.productType
    });
    rudderstackTrack("Promotion Viewed", {
      is_app: Boolean(window.MOBILE_APP),
      location: "guest_email_modal",
      platform: window.MOBILE_APP_PLATFORM || "web",
      text: `${this.percentOff} OFF YOUR FIRST ${this.upsellText} ORDER!`,
      type: "upsell_modal",
      url: window.location.href
    });
  },
  methods: {
    onModalClose() {
      writeCookie(`email_cross_sell_modal_${this.productType}`, "true", 2147483647);
    },
    sendEmailSubmittedEvent() {
      submitDtfEvent("guest_email_modal_email_submitted", {
        page: "pdp",
        email: this.email,
        product: this.productType,
        promotion_name: this.promotionName
      });
    },
    setCoupon() {
      this.coupon = this.isTransfers ? "DTF20" : "STICKERS30";
    },
    closeModal() {
      this.$refs.commonModal.close();
    },
    async submitEmail() {
      const { promotionName, email } = this;
      this.emailError = !this.emailPattern.test(email);

      if (this.emailError) return;

      try {
        this.isLoading = true;
        const { success } = await api.dtf.createMailChimpEntry({
          promotions: { email, promotion_name: promotionName }
        });
        if (success) {
          this.setCoupon();
          this.sendEmailSubmittedEvent();
        }
      } catch (error) {
        window.Honeybadger?.notify("Error creating mail chimp entry", {
          params: { error: JSON.stringify({ ...error, email, promotionName }) }
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.email-upsell-modal {
  $this: &;

  display: flex;
  justify-content: center;
  align-items: center;

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    text-align: center;
    background-color: $white;
    padding: 32px;
    height: 505px;
    width: 375px;
    min-width: 375px;
    padding-bottom: 16px;

    &--stickers {
      @include with-desktop {
        padding-bottom: 42px;
      }

      #{$this}__promo-code {
        border-color: $promo-blue-light;
        color: $text-black;
        background-color: $promo-blue-lighter;
      }

      #{$this}__submit-btn {
        background-color: $promo-blue-light;
      }

      #{$this}__footer {
        justify-content: center;
      }

      #{$this}__delivery-info {
        margin: 0;
      }
    }

    @include with-tablet {
      height: 479px;
      width: 428px;
      min-width: 428px;
    }

    @include with-desktop {
      height: 455px;
      width: 450px;
      min-width: 450px;
      padding: 32px;
      padding-bottom: 16px;
    }
  }

  &__banner {
    display: none;

    @include with-desktop {
      display: block;
      width: 300px;
      height: 455px;
      object-fit: cover;
      object-position: left;
    }

    @include with-large-desktop {
      width: 455px;
      height: 455px;
      object-position: center;
    }
  }

  &__brand-logo {
    flex-shrink: 0;
    height: 30px;
    width: 290px;
  }

  &__offer-title {
    font-family: $digital-assets-font-family;
    font-size: 44px;
    margin: 20px 0 0 0;
    font-weight: 400;
    letter-spacing: -3px;
    color: $crafterpay-gray-modern-800;

    &--highlight {
      font-weight: 800;
    }
  }

  &__offer-subtitle {
    font-family: $digital-assets-font-family;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 24px;
    color: $crafterpay-gray-modern-800;
  }

  &__email-form {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 15px;
  }

  &__email-input {
    font-size: 16px;
    margin-bottom: 16px;
    border: none;
    border-bottom: 1px solid $crafterpay-gray-modern-400;
    color: $crafterpay-gray-modern-800;
    padding: 10px 2px;

    &:focus {
      outline: none;
      border-bottom: 2px solid $text-black;
      margin-bottom: 15px;
    }

    &--error {
      border-bottom-color: $red-bright;
    }
  }

  &__spinner {
    width: 20px;
  }

  &__submit-btn {
    background-color: $dropzone-highlight;
    color: $white;
    width: 100%;
    height: 56px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    &:disabled {
      background-color: $crafterpay-gray-modern-400;
      cursor: not-allowed;
    }

    &:hover {
      filter: brightness(115%);
    }
  }

  &__btn-icon {
    position: absolute;
    color: $white;
    height: 25px;
    width: 25px;
    right: 20px;
  }

  &__coupon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__coupon-header {
    font-family: $digital-assets-font-family;
    font-size: 32px;
    font-weight: 700;
    line-height: 37px;
    letter-spacing: -1px;
    text-align: center;
    margin-top: 0;
  }

  &__promo-code {
    font-family: $font-family-wider;
    height: 72px;
    padding: 20px 56px;
    gap: 24px;
    border-radius: 8px;
    border: 2px dashed $dropzone-highlight;
    font-size: 32px;
    line-height: 32px;
    color: $dropzone-highlight;
    background-color: $dtf-pink-light;
    font-weight: 900;
    display: flex;
    align-items: center;
    margin-bottom: 15px;
  }

  &__coupon-sub-text {
    font-family: $digital-assets-font-family;
    font-size: 14px;
    font-weight: 700;
    line-height: 16.94px;
    text-align: center;
  }

  &__error {
    color: $red-bright;
    font-size: 14px;
    text-align: left;
    position: relative;
    top: -13px;
  }

  &__agreement {
    color: $crafterpay-gray-modern-800;
    font-size: 12px;
    text-align: justify;
    text-decoration: none;

    &--highlight-link {
      color: inherit;
      text-decoration: underline;
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100px;
  }

  &__delivery-info {
    font-size: 13px;
    margin-bottom: 5px;
    font-weight: 700;
    color: $green-emerald-small-light;
    display: flex;
    width: 100%;
    align-items: center;
  }

  &__delivery-icon {
    height: 38px;
    width: 38px;
  }

  &__learn-more {
    color: $crafterpay-gray-modern-800;
    font-size: 14px;
    text-decoration: none;

    &--highlight-link {
      color: inherit;
      text-decoration: underline;
      font-weight: bold;
    }
  }
}
</style>
